import { normalizeWorkspaceDoc, type ButtonSpec, type CommandSpec, type HostApi, type PluginModule } from '@easydb/shared';
import {
  EDB_EXTENSION,
  canPickFolder,
  canSaveInPlace,
  ensureWritable,
  fileInFolder,
  forgetFolder,
  forgetHandle,
  listWorkspaceFiles,
  pickFileToOpen,
  pickFolder,
  readBytes,
  rememberFolder,
  rememberHandle,
  rememberedFolder,
  rememberedHandle,
} from '../db/edb/file-handle.js';
import { activeEdbName, adoptedFileName, reloadWithoutSpace, reloadWithSpace, setActiveEdbName } from '../db/edb/session.js';
import { saveErrorMessage, saveErrorSummary } from '../db/edb/save-error.js';
import { changedHere, clearWriteDeclined, compareWithFile, factsOfHandle, holdsUnsavedWork, markWriteDeclined, markLocalChanges, readStamp, recordAgreement, verdictFor, writeDeclined } from '../db/edb/file-stamp.js';
import { clearAppProgress, setAppProgress } from '../chrome/app-progress-signal.js';
import { SETTINGS_CHANGED_EVENT, settingsChangeAffects, type SettingsChangedDetail } from '../db/settings-events.js';
import { adoptDeviceFolder, deviceStorage, disconnectDeviceState, onDeviceStateError } from '../db/edb/device-state.js';
import { installDeviceStore } from '../db/user-settings.js';
import { cloneWorkspace } from '../db/clone-workspace.js';
import { deleteWorkspace } from '../db/delete-workspace.js';
import { createAutosavePolicy, type AutosavePolicy } from '../db/edb/dirty.js';
import { edbBridge, edbHandle, setEdbHandle, storeBridge } from '../db/edb/active-bridge.js';
import { copyWorkspace } from '../db/edb/convert.js';
import { syncActiveWorkspace, syncFolder } from '../db/edb/folder-sync.js';
import { confirmDataLoss, newerSide, USE_BROWSER_COPY, USE_FILE_COPY } from '../db/edb/copy-choice.js';
import { openLocalDataDialog } from '../dialogs/local-data-dialog.js';
import { describeActiveOutcome } from '../db/edb/active-file-sync.js';
import { createEdbBridge } from '../db/edb/worker-bridge.js';
import type { PeekedWorkspace } from '../db/edb/protocol.js';
import { BROWSER_SIDE, compareCopies, fileSide, type CopyFacts } from '../db/edb/copy-facts.js';
import { askAboutFile, mergeWithFile, type MergeContext } from '../db/edb/merge-file.js';
import { PULL, PUSH } from '../db/edb/merge-answers.js';
import { countWorkspaceContents } from '../db/delete-workspace.js';
import { clearFolderIndex, isEmptyWorkspace, listLabels, workspaceLabel } from '../db/edb/folder-index.js';
import { createIpcDataStore } from '../db/data-store-bridge.js';
import { adoptEdbFile, placeForNextBoot, workspaceFolder, type EdbTarget } from '../db/edb/new-file.js';
import { adoptFolderFile, askForComparison, clearPendingSpaceRequest, pendingSpaceRequest, reloadActiveFromFile, settleCopyQuestion, takeComparisonRequest, takeCopyQuestion } from '../db/edb/space-adopt.js';
import { alsoWroteNote, withoutTheirOwnFile, writableWholesale } from '../db/edb/one-per-file.js';
import { installWriteGuard, writeUserBytes, type WriteGuardDeps } from '../db/edb/guarded-write.js';
import { describeHolding, firstWarning, refusedNote, secondWarning, type Holding } from '../db/edb/empty-write.js';
import { dangerConfirm } from '../dialogs/danger-confirm.js';
import { freeWorkspaceId, mayCreateWorkspaceIn, spaceFileName, workspaceIdFromFileName } from '../db/edb/space-resolve.js';

/**
 * The `.edb` file surface in the browser: Open, Save, the autosave switch and the
 * workspace folder.
 *
 * **These are palette commands, not a menu.** They sat behind a "File" footer
 * button until v0.0.402, which was a second navigation model for five items the
 * palette already indexed — and every other footer button belongs to the workspace
 * (Views, Import, Export), so a global File menu was in the wrong place besides.
 * Ctrl+K and a word is the whole interaction now. The header's Save button stays,
 * because Save is the one of these done often enough to want a mouse target, and
 * because the unsaved dot has to live somewhere always visible.
 *
 * Save and autosave are offered whether or not this workspace has a file yet. They
 * used to appear only once one had been adopted, which read as "this app cannot
 * save" to anyone who had connected a folder and never opened a file — the one
 * state in which saving is both possible and not yet done.
 *
 * TWO items are deliberately absent, and both were removed because something else
 * already did the job:
 *
 * - **New .edb file** — New workspace → Advanced creates a workspace in its own
 *   file (`chrome/workspace-actions.ts`).
 * - **Save As** — it existed to write the workspace somewhere else under another
 *   name, which is now the one thing that MUST not happen: a file's name is the
 *   workspace inside it (`spaceFileName` / `workspaceIdFromFileName`), so
 *   `sales.edb` holding the workspace `q3` is a file Open cannot make sense of. A
 *   copy comes from New workspace → "Clone everything" and then Save, which writes
 *   the clone under its own name.
 *
 * A plugin, not core, because everything that can be a plugin in this app is
 * one. It registers nothing at all outside a browser that can host the worker,
 * so an Electron build — which has its own file operations in `electron-db` —
 * never shows two competing sets of buttons.
 */

export const meta: NonNullable<PluginModule['meta']> = {
  id: 'edb-file',
  name: 'Workspace files (.edb)',
  type: 'source',
  version: '0.1.0',
  description: 'Keeps a workspace in a real SQLite .edb file that you Open and Save yourself.',
  author: 'Marc Cawood',
  // `meta.icon` is drawn with `unsafeHTML`, so it takes markup — NOT a Material
  // Icons ligature. The same drawing as `electron-db`, because the two are the
  // same idea on two platforms.
  icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5"/><path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6"/></svg>',
  repo: 'https://github.com/cawoodm/easydbaccess/blob/main/packages/renderer/src/plugins/edb-file.ts',
};

const AUTOSAVE_KEY = 'autosave';

/**
 * The palette group these commands appear under.
 *
 * "File" because that is the word the user came looking for, and because the
 * palette buckets by group heading rather than by which plugin registered what.
 */
const FILE_GROUP = 'File';

/**
 * What `load()` needs out of `init()`'s closure.
 *
 * `load` is a separate export and cannot see the autosave policy, which is why the
 * stored autosave flag used to be read at boot and then only toasted about, never
 * applied — autosave did not actually resume after a reload, while the menu said
 * "Turn on autosave" as if it had never been on.
 */
let session: { autosave: AutosavePolicy; refreshSaveButton: () => void; refreshFileCommands: () => Promise<void>; compareWithOwnFile: () => Promise<void>; ownFile: () => Promise<FileSystemFileHandle | null> } | null = null;

/**
 * The name the throwaway worker opens a file under while Overwrite rewrites it.
 * Not a real workspace file, and deliberately not any name a user could pick, so
 * its OPFS mirror cannot land on top of a database that matters.
 */
const SYNC_SCRATCH = '__edb-sync-scratch.edb';

/** The escape hatch in the Open list, for a file outside the workspace folder. */
const BROWSE = 'Another file…';

/** The name the throwaway worker opens a DROPPED file under. Never a real file. */
const DROP_SCRATCH = '__edb-drop-scratch.edb';

/**
 * The name the throwaway worker builds a single-workspace database under, when a
 * Save has to strip a database of several down to the one its file is named for.
 * Never a real file, for the same reason as the two above.
 */
const SPLIT_SCRATCH = '__edb-split-scratch.edb';

/** The two ways a dropped file can meet a workspace of the same name. */
const OVERWRITE_LOCAL = 'Replace the one here';
const KEEP_BOTH = 'Keep both, under a new name';

/** As much of a workspace record as a dropped file is asked for. */
interface WorkspaceDoc {
  id: string;
  /** What the user calls it, when it carries one. Never derived from the id. */
  title?: string | undefined;
  /** What that workspace holds inside the file — for the replace-or-keep-both question. */
  tables: number;
  views: number;
}

/**
 * The workspace records in a peeked file, as ids, titles and sizes.
 *
 * `peekWorkspaces` answers raw documents, and a file may have been written by
 * anything: a record with no usable id is dropped rather than trusted. Through
 * `normalizeWorkspaceDoc`, so a file written before v0.0.506 hands its `name`
 * over as the title rather than losing it.
 */
function workspaceDocs(raw: readonly PeekedWorkspace[]): WorkspaceDoc[] {
  return raw
    .map((w) => normalizeWorkspaceDoc(w.doc))
    .map((doc, i) => ({ id: String(doc['id'] ?? ''), ...(typeof doc['title'] === 'string' && doc['title'] ? { title: doc['title'] } : {}), tables: raw[i]!.tables, views: raw[i]!.views }))
    .filter((w) => w.id !== '');
}

/**
 * The files a drop hands over.
 *
 * `dataTransfer.files` is the ordinary answer; `items` is the fallback some
 * browsers give for a drag that started inside the page. Each importer carries its
 * own copy of this — a five-line reader is not worth a shared module that every
 * plugin then depends on.
 */
function filesFrom(event: DragEvent): File[] {
  const dt = event.dataTransfer;
  if (!dt) return [];
  if (dt.files && dt.files.length > 0) return Array.from(dt.files);
  return Array.from(dt.items ?? [])
    .filter((i) => i.kind === 'file')
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null);
}

/**
 * The answers offered when a save has nowhere to go. Compared by value.
 *
 * No explicit "Cancel" among them: `dialogs.choice` already carries a dismiss
 * button, and adding one produced two Cancels side by side.
 */
const CONNECT_FOLDER = 'Connect a folder…';
const TURN_OFF_AUTOSAVE = 'Turn off autosave';

/**
 * Said when a Save has no folder behind it.
 *
 * The workspace is NOT at risk — it is a real SQLite database in the OPFS pool and
 * it survives a reload — so the message leads with that and then says what a folder
 * would add. Anything shorter reads as "your data is not saved".
 */
const NO_FOLDER_YET = 'This workspace is stored in this browser, and stays there. To keep it in a file you can move, back up or open on another machine, connect a workspace folder.';

/**
 * Said when this browser cannot hand the app a file at all.
 *
 * The FileSystem Access API is Chromium-only, and with the IndexedDB dump gone
 * there is no second place a Save could put the bytes. The workspace is still
 * durable — it is a real database in the OPFS pool and it survives a reload —
 * so this is about PORTABILITY, and the message says which.
 */
const NO_FILE_ACCESS =
  'This browser cannot give easyDBAccess a file to write to, so this workspace cannot be saved to disk here. It is still stored in this browser and survives a reload. To keep a copy you can move, open this workspace in Chrome or Edge.';

/** Whether a Save could reach a file at all in this browser. */
function canReachAFile(): boolean {
  return canSaveInPlace() || canPickFolder();
}

/** Only offer this where a worker can run. Electron has its own file operations. */
function supported(): boolean {
  return typeof Worker === 'function' && !window.easydb?.store;
}

export function init(api: HostApi): void {
  if (!supported()) return;

  // The same record the palette's "Turn on autosave" writes — one setting, two
  // ways in. The command is where you reach for it mid-edit; this is where you
  // find out it exists at all, which a command hidden behind a palette search
  // never was. Device-local, because a `.edb` travels between machines and
  // which of them writes on a timer is not a property of the workspace.
  api.ui.registerSettings(meta.id, 'Files', [
    {
      key: AUTOSAVE_KEY,
      label: 'Autosave',
      type: 'boolean',
      default: false,
      scope: 'user',
      description: 'Write this workspace back to its .edb file a moment after every change, instead of waiting for Save.',
      help:
        'Autosave needs somewhere to write. A workspace that has never been saved has no file, so the first tick asks for a folder — ' +
        'and if you decline, autosave turns itself back off rather than asking again on every change. ' +
        'An import or a bulk delete counts as one change, not thousands: the file is written once, when the batch finishes.',
    },
  ]);

  /**
   * Where a save landed.
   *
   * `no-handle` is not a failure — it is the one case the user can answer, and
   * the two callers answer it differently. See {@link save} and the autosave
   * policy below.
   */
  type SaveTarget = 'file' | 'no-handle' | 'none';

  /** Where a save landed, and any file it wrote besides the one it was asked to. */
  interface SaveResult {
    where: SaveTarget;
    /** Files given to workspaces that had none — see {@link fileTheStranded}. */
    alsoWrote: string[];
  }

  /**
   * A database holding just `workspaceId`, as bytes.
   *
   * Built in a THROWAWAY worker on the memory substrate, because the pool is
   * exclusive origin-wide and the live session holds it — the same arrangement
   * `overwriteInFile`, `bringWorkspaceIn` and `buildEdbFile` all rely on. The live
   * database is never touched: this reads out of it and writes into the scratch.
   *
   * The source store is scoped explicitly rather than taken as `api.store`, so the
   * same function serves the ACTIVE workspace and a passenger the tab is not
   * looking at.
   */
  async function workspaceOnlyBytes(workspaceId: string, label: string): Promise<Uint8Array> {
    const scratch = createEdbBridge();
    try {
      await scratch.open(null, SPLIT_SCRATCH);
      await copyWorkspace(
        createIpcDataStore(storeBridge(), () => workspaceId),
        createIpcDataStore(scratch, () => workspaceId),
        workspaceId,
        (p) => setAppProgress({ label, detail: p.label }),
      );
      return await scratch.export();
    } finally {
      clearAppProgress();
      scratch.terminate();
    }
  }

  /**
   * The bytes this workspace's file should hold: this workspace, and nothing else.
   *
   * `export()` — the whole database — is still the fast path, and the one every
   * correct workspace file takes, because a database holding one workspace already
   * IS that file. A database holding several has to be filtered, which costs a copy
   * through a scratch worker.
   *
   * This is the fix for two workspaces sharing a file. `export()` alone wrote
   * the project index's entire workspace list into whichever file the active
   * workspace was named after. See `db/edb/one-per-file.ts`.
   */
  async function bytesForFile(bridge: NonNullable<ReturnType<typeof edbBridge>>): Promise<Uint8Array> {
    const ids = (await api.store.workspaces.find()).map((w) => w.id);
    const active = api.workspaceId();
    if (!active || writableWholesale(ids)) return bridge.export();
    return workspaceOnlyBytes(active, `Saving ${spaceFileName(active)}`);
  }

  /**
   * Give every other workspace in this database a file of its own.
   *
   * Not tidiness — it is what stops the filtered write above losing data. A
   * workspace that arrived in this database from New workspace, a dropped `.edb` or
   * the legacy import has never had a file; its only copy on disk used to be the
   * passenger seat inside the active workspace's file. Now that file holds one
   * workspace, so without this the passenger would exist nowhere but this browser —
   * and `reloadActiveFromFile` replaces the database with the file's contents.
   *
   * Only in a connected FOLDER: a lone file handle is permission to write that one
   * file and nothing else, so there is nowhere to put them. Idempotent, so the
   * second save finds every file already there and writes nothing.
   */
  /**
   * The ones that hold something the user put there.
   *
   * Tables and view instances only — `isEmptyWorkspace`'s rule, so "empty" means
   * the same thing here as it does at the conflict prompts. Rows are not counted:
   * a table is enough to make a workspace worth a file.
   */
  async function onlyNonEmpty(ids: readonly string[]): Promise<string[]> {
    const out: string[] = [];
    for (const id of ids) {
      // A count that cannot be taken must not silently drop a workspace: without a
      // number, the safe answer is that it holds something.
      const counted = await countWorkspaceContents(storeBridge(), id, { countRows: false }).catch(() => null);
      if (!counted || !isEmptyWorkspace(counted)) out.push(id);
    }
    return out;
  }

  /**
   * Every write to a file the user owns goes through here.
   *
   * The rule itself is in `db/edb/empty-write.ts` and the door in
   * `db/edb/guarded-write.ts`; this is only the wiring — which bridge peeks the
   * bytes, which dialog asks, what the user is told when a write is stopped.
   *
   * Answers whether the bytes were written. Every caller must treat `false` as
   * "nothing happened": no stamp recorded, no "saved" toast, and above all no
   * `markClean`, which would turn a write the user STOPPED into the same data loss
   * on the next reload.
   */
  function writeGuardDeps(): WriteGuardDeps {
    return {
      // A missing bridge means nothing can be measured. Answering with a holding
      // that looks USED keeps today's behaviour — the write goes ahead — rather
      // than making an unmeasurable moment look like the dangerous one.
      peek: async (b) => {
        const live = edbBridge();
        return live ? await live.peekWorkspaces(b) : [{ tables: 1, views: 0 }];
      },
      confirm: (f, onDisk: Holding, writing: Holding) =>
        dangerConfirm({
          title: 'This save would empty the file',
          message: firstWarning(f, onDisk, writing),
          secondMessage: secondWarning(f, onDisk),
          confirmLabel: 'Replace the file anyway',
          secondConfirmLabel: `Delete ${describeHolding(onDisk)}`,
        }),
      onRefused: (f) => api.ui.dialogs.toast(refusedNote(f), { kind: 'info', title: 'Save stopped' }),
    };
  }

  async function writeUserFile(handle: FileSystemFileHandle, bytes: Uint8Array, file: string, reason: string): Promise<boolean> {
    return writeUserBytes(handle, bytes, { file, reason }, writeGuardDeps());
  }

  // Every OTHER writer of a user's file — `new-file.ts` building one from a
  // picker — reaches the same guard through this, because it cannot reach the
  // dialog or the live worker itself.
  installWriteGuard(writeGuardDeps());

  // The device layer reads and writes through the connected folder from here on.
  // Installed unconditionally: with no folder connected the shim is `localStorage`
  // and nothing behaves differently, so no caller has to ask which it is.
  installDeviceStore(deviceStorage());
  // A folder that stops taking the device state is invisible otherwise: this
  // browser keeps working on its local copy, and only the NEXT device finds out,
  // by being wrong.
  onDeviceStateError((message) => api.ui.dialogs.toast(`The workspace folder would not take the device settings: ${message}`, { kind: 'warning', title: 'Local Data' }));

  async function fileTheStranded(opts: { skipEmpty?: boolean; includeActive?: boolean } = {}): Promise<string[]> {
    const dir = await connectedFolder();
    if (!dir) return [];
    const active = api.workspaceId();
    if (!active) return [];
    const ids = (await api.store.workspaces.find()).map((w) => w.id);
    // A Save excludes the active workspace because the Save itself is writing
    // that file. A SYNC writes nobody's file otherwise, so the one workspace the
    // user is looking at would be the only one left without a home. Passing no
    // active id includes it in the same loop, which is the whole difference —
    // and, unlike routing it through Save, it does NOT make the tab adopt the
    // file. That mattered: a file holds one workspace since v0.0.427, so a tab
    // that adopted its own file came back from the next load with every OTHER
    // workspace gone from the UI.
    let missing = withoutTheirOwnFile(ids, opts.includeActive ? '' : active, await listWorkspaceFiles(dir));
    // A Save writes every stranded workspace, empty or not: the file it is about
    // to write holds one workspace afterwards, so a passenger with no file of its
    // own would exist nowhere. A SYNC has no such deadline, and an empty shell —
    // which `?space=x` makes on every private window — is not worth a file.
    if (opts.skipEmpty) missing = await onlyNonEmpty(missing);
    const wrote: string[] = [];
    for (const id of missing) {
      const name = spaceFileName(id);
      const handle = await fileInFolder(dir, name, true);
      if (!handle) continue;
      if (!(await writeUserFile(handle, await workspaceOnlyBytes(id, `Giving ${name} its own file`), name, 'Giving a workspace its own file'))) continue;
      const facts = await factsOfHandle(handle);
      if (facts) recordAgreement(name, facts);
      wrote.push(name);
    }
    return wrote;
  }

  /**
   * The context the merge machinery needs, or null where it cannot run.
   *
   * Null on a tab with no workspace and on a build with no worker bridge — the
   * desktop, where this whole plugin registers nothing anyway.
   */
  function mergeContext(handle: FileSystemFileHandle): MergeContext | null {
    const bridge = edbBridge();
    const workspaceId = api.workspaceId();
    if (!bridge || !workspaceId) return null;
    return { handle, file: activeEdbName(), workspaceId, store: api.store, bridge, dialogs: api.ui.dialogs };
  }

  /**
   * May we write over this file?
   *
   * The file's timestamp and size are recorded at every moment our copy and it
   * agreed — the import, and every write we made (`recordAgreement`). If the file
   * no longer matches that stamp, SOMETHING ELSE WROTE IT: another origin, another
   * profile, another machine through a synced folder. Writing anyway destroys that
   * work, and it is the one loss the app cannot undo.
   *
   * Only a positive difference stops the write. No stamp at all answers `unknown`
   * — a file we have never agreed with, which is every first save into a new file
   * — and there is nothing to be alarmed about in a difference nobody can measure.
   * `ahead` is the ordinary Save: our copy has changes, the file has not moved.
   *
   * `auto` is the autosave timer. It gets the same question but asks it once per
   * state of the file: the verdict does not improve by being declined, and a modal
   * every thirty seconds is worse than the problem it reports. A manual Save always
   * asks, because the user just asked for something and deserves an answer.
   *
   * **Four answers, not two.** "Keep the file" and "keep mine" were the only two
   * the file layer could act on while a `.edb` was one indivisible blob, and both
   * of them throw away somebody's work whenever BOTH copies were edited — which is
   * the ordinary case for a folder shared between two machines. Take newest and
   * Compare settle it table by table instead; see `merge-file.ts`.
   */
  /**
   * An EMPTY workspace may never replace a file that holds something.
   *
   * Independent of the stamp, and that is the whole point of it. Every other
   * guard here works off `compareWithFile`, which answers `unknown` when this
   * origin has no record of the file — and `unknown` used to mean "go ahead". So
   * the one state the app must never write out was the one it wrote out without
   * asking: a tab that came up on an empty shell of a workspace whose data is in
   * the file (see `db/edb/space-resolve.ts`) would, on the next autosave tick,
   * put that empty database over it. The bytes it replaced were the only copy.
   *
   * **The QUESTION is not asked here.** It is asked at the door, by
   * `writeUserFile` → `guarded-write.ts`, in two red steps — which is what the
   * bug report asked for and, being at the door, covers every writer rather
   * than this one path. A single yes/no here asked FIRST and so pre-empted it:
   * the two-step guard could never open, because the write was already refused
   * or already allowed by the time the door saw it.
   *
   * What is left is the one thing the door cannot know: whether this is the
   * autosave TIMER, which must not put a modal on screen every thirty seconds
   * for a file the user has already declined to overwrite in this exact state.
   * `persist` records that decline; this reads it.
   */
  async function mayWriteNothingOverIt(file: string, now: { mtime: number; size: number }, auto: boolean): Promise<boolean> {
    if (!auto || !writeDeclined(file, now)) return true;
    const mine = await openContentsFacts();
    return mine.tables !== 0 || (mine.views ?? 0) !== 0;
  }

  async function mayOverwriteFile(handle: FileSystemFileHandle, auto: boolean): Promise<boolean> {
    const file = activeEdbName();
    const now = await factsOfHandle(handle);
    // Unreadable is not "changed": the permission check above already passed, so
    // this is a transient failure and the write is the user's own instruction.
    if (!now) return true;
    if (!(await mayWriteNothingOverIt(file, now, auto))) return false;
    const verdict = compareWithFile(readStamp(file), now);
    if (verdict !== 'file-newer' && verdict !== 'conflict') return true;
    if (auto && writeDeclined(file, now)) return false;

    const ctx = mergeContext(handle);
    const sides = compareCopies([
      { label: BROWSER_SIDE, facts: { ...(await openContentsFacts()), ...changedHere(file) }, newer: newerSide(verdict) === 'browser' },
      { label: fileSide(file), facts: { ...(await countsInFileHandle(handle)), ...now }, newer: newerSide(verdict) === 'file' },
    ]);
    const lead = `${file} has been written since this tab last saved it — by another tab, another browser or another machine. Saving now would replace that work.`;
    // Without a bridge there is nothing to compare WITH, so the old pair of
    // answers is still the whole truth and offering four would be a lie.
    if (!ctx) return (await api.ui.dialogs.choice(`${lead}${sides}`, [PUSH, PULL], 'Save')) === PUSH;

    const answer = await askAboutFile(ctx, lead, sides);
    if (answer === 'push') return true;
    if (answer === 'newest' || answer === 'compare') {
      const result = await mergeWithFile(ctx, answer === 'newest' ? 'newest' : 'compare');
      // A merge that ran leaves this database holding the settled copy, so the
      // save it interrupted is now the right thing to do: it writes that copy out
      // and records the agreement. A merge the user backed out of does not.
      if (result.merged) return true;
      markWriteDeclined(file, now);
      return false;
    }
    if (answer === 'pull') {
      // Replacing this database with the file is a reload, so nothing after this
      // runs — including the save that asked.
      await reloadActiveFromFile(file);
      return false;
    }
    // Dismissing means the same as leaving the file: the safe answer, and the only
    // one that touches nothing.
    markWriteDeclined(file, now);
    api.ui.dialogs.toast(`Left ${file} as it is. Run "Sync workspace folder" to read it in.`, { kind: 'info', title: 'Save' });
    return false;
  }

  /**
   * The file this tab is backed by — the session's handle, or the one in the
   * connected folder that the adopted-file marker names.
   *
   * The fallback is what makes "Compare them…" work at all. That answer is given
   * BEFORE a reload (the browser's copy has to be open before the two can be
   * compared), so the comparison runs in the next page's `load()` — and at that
   * moment the session handle is still empty: `load()` restores it further down,
   * after the two pending questions. Boot's own path never had one to restore,
   * because taking the browser's copy remembers no handle. Either way the answer
   * was "there is no file to compare it with", about the very file the user had
   * just been shown the size and date of, and running the Compare command by hand
   * a second later worked — by then the handle was back.
   *
   * The folder is asked WITHOUT a prompt: a reload has no user gesture, so a
   * `requestPermission` here would be refused by the browser anyway. A folder the
   * user connected in this session is already granted, which is every case where
   * this question can arise.
   *
   * **Every handle is checked against the marker's NAME, and that is the whole of
   * a reported data loss.** The two are remembered in different places and neither
   * knew about the other: the marker is `localStorage`, per tab; the handle is ONE
   * IndexedDB slot (`CURRENT` in `file-handle.ts`), origin-wide, overwritten by
   * whichever file was opened last in any tab. So a tab whose marker said
   * `powerplants.edb` booted, picked up the handle the other tab had left for
   * `default.edb`, and from then on Save wrote the workspace into `default.edb`
   * while the toast — which reads the marker — said `powerplants.edb`. The file the
   * user was told about was never written, and the file that WAS written already
   * held somebody else's workspace.
   *
   * A mismatch is not repaired by taking the other file: it is dropped, and the
   * folder is asked for the file the marker actually names. When even that fails
   * there is NO handle, so Save asks where to write rather than guessing — the one
   * answer that cannot overwrite a file the user never named.
   */
  async function ownFile(): Promise<FileSystemFileHandle | null> {
    const file = adoptedFileName();
    if (!file) return null;
    const open = edbHandle();
    if (open) {
      if (sameFile(open.name, file)) return open;
      // Belongs to another workspace's file. Drop it, or every later reader of
      // `edbHandle()` inherits the same wrong answer.
      setEdbHandle(null);
    }
    // The remembered slot, but only when it names THIS file. It is the sole record
    // of a file kept outside the folder — the "Another file…" route, which the
    // lookup below cannot reach — so it has to be asked; it is just no longer
    // believed on the strength of existing.
    const remembered = await rememberedHandle();
    if (remembered && sameFile(remembered.name, file)) {
      setEdbHandle(remembered);
      return remembered;
    }
    const dir = await rememberedFolder();
    if (!dir || !(await ensureWritable(dir, false))) return null;
    const found = await fileInFolder(dir, file, false);
    // Kept for the rest of the session: Save and Sync look the file up the same
    // way, and the tab is demonstrably connected to it.
    if (found) setEdbHandle(found);
    return found;
  }

  /**
   * Two file names for one file.
   *
   * Case-insensitive, like every other comparison in this layer: `Sales.edb` and
   * `sales.edb` are one file on Windows, and treating them as two would throw away
   * a perfectly good handle on every boot.
   */
  function sameFile(a: string, b: string): boolean {
    return a.toLowerCase() === b.toLowerCase();
  }

  /**
   * Compare this workspace with its own file, on demand.
   *
   * The same machinery the two clash prompts reach, offered as a command so it
   * does not have to be provoked. Two tabs sharing a folder is the case this
   * whole feature is for, and in it the user usually knows the other machine has
   * been working before this one has noticed — waiting for a Save to collide is
   * waiting for the moment it is hardest to think about.
   */
  async function compareWithOwnFile(): Promise<void> {
    const handle = (await ownFile()) ?? null;
    if (!handle) {
      await api.ui.dialogs.alert('This workspace is stored in this browser, so there is no file to compare it with. Save it into a folder first.', 'Compare');
      return;
    }
    if (!(await ensureWritable(handle, true))) {
      await api.ui.dialogs.alert('easyDBAccess is not allowed to read that file. Run the "Connect workspace folder" command to grant it again.', 'Compare');
      return;
    }
    const ctx = mergeContext(handle);
    if (!ctx) return;
    await mergeWithFile(ctx, 'compare');
  }

  /** What the OPEN database holds, for the side-by-side. Best effort. */
  async function openContentsFacts(): Promise<CopyFacts> {
    const workspaceId = api.workspaceId();
    if (!workspaceId) return {};
    try {
      const c = await countWorkspaceContents(storeBridge(), workspaceId, { countRows: false });
      return { tables: c.tables, views: c.views };
    } catch {
      return {};
    }
  }

  /** Tables and views inside a file, totalled across every workspace it holds. */
  async function countsInFileHandle(handle: FileSystemFileHandle): Promise<CopyFacts> {
    const live = edbBridge();
    if (!live) return {};
    try {
      const bytes = new Uint8Array(await (await handle.getFile()).arrayBuffer());
      const inside = workspaceDocs(await live.peekWorkspaces(bytes));
      if (inside.length === 0) return {};
      return {
        tables: inside.reduce((n, w) => n + w.tables, 0),
        views: inside.reduce((n, w) => n + w.views, 0),
      };
    } catch {
      return {};
    }
  }

  /**
   * Write the workspace to the user's file.
   *
   * The ONLY durable copy this makes outside the browser. There is no
   * second, IndexedDB-held copy any more: the OPFS pool already survives a
   * reload, so a dump beside it was a second answer to a question already
   * answered, and it made "saved" mean two different things.
   *
   * Save does not hand over a download either. A save is about the workspace
   * surviving; producing a copy to give away is what Export is for.
   *
   * `auto` marks the autosave timer's call — see `mayOverwriteFile`, the one
   * place the two differ.
   */
  async function persist(opts: { auto?: boolean } = {}): Promise<SaveResult> {
    const bridge = edbBridge();
    if (!bridge) return { where: 'none', alsoWrote: [] };
    // Through `ownFile`, never `edbHandle()` straight: that slot is origin-wide and
    // may hold another workspace's file, and a Save is exactly where writing to the
    // wrong one costs the user their work. The toast below names the MARKER, so the
    // two have to be the same file or the message is a lie.
    const handle = await ownFile();
    if (!handle) return { where: 'no-handle', alsoWrote: [] };
    if (!(await ensureWritable(handle, true))) {
      await api.ui.dialogs.alert('easyDBAccess is not allowed to write that file. Run the "Connect workspace folder" command to grant it again.', 'Save');
      return { where: 'none', alsoWrote: [] };
    }
    // Before the bytes go out, not after: this is the only check standing between
    // an autosave tick and somebody else's afternoon.
    if (!(await mayOverwriteFile(handle, opts.auto === true))) return { where: 'none', alsoWrote: [] };
    // The empty-write guard, which is the LAST word: a refusal here means nothing
    // happened, so no stamp is recorded and the workspace stays dirty.
    if (!(await writeUserFile(handle, await bytesForFile(bridge), activeEdbName(), 'Save'))) {
      // Remember the refusal against the file AS IT IS, so the autosave timer does
      // not put the same red question up every thirty seconds. A manual Save asks
      // again regardless — `mayWriteNothingOverIt` only reads this for the timer.
      const declined = await factsOfHandle(handle);
      if (declined) markWriteDeclined(activeEdbName(), declined);
      return { where: 'none', alsoWrote: [] };
    }
    // The file is ours again, so an earlier "leave it alone" no longer applies.
    clearWriteDeclined(activeEdbName());
    // The file now holds this workspace, so record what it looks like. That is what
    // lets a later sync tell "someone else wrote this file" from "we wrote it
    // ourselves" — see `file-stamp.ts`.
    const facts = await factsOfHandle(handle);
    if (facts) recordAgreement(activeEdbName(), facts);
    // After the file the user asked for, never before: a failure here must not cost
    // them the save they came for.
    return { where: 'file', alsoWrote: await fileTheStranded() };
  }

  /**
   * Remember the folder the user just granted, and put the commands in step with it.
   *
   * One function for all three places that can connect a folder — the folder command
   * and both "there is nowhere to save this" answers. When only the first of them
   * refreshed, saving into a brand-new folder left the palette still offering to
   * *connect* one.
   */
  async function connectFolder(dir: FileSystemDirectoryHandle): Promise<void> {
    await rememberFolder(dir);
    await adoptDeviceFolder(dir);
    await refreshFileCommands();
  }

  /** The folder this app may already write in. Never asks for one. */
  async function connectedFolder(): Promise<FileSystemDirectoryHandle | null> {
    const dir = await rememberedFolder();
    return dir && (await ensureWritable(dir, true)) ? dir : null;
  }

  const autosave = createAutosavePolicy({
    // A reload does not make work saved. The policy's own flag is built fresh on
    // every load and starts at false, so the red dot went out on every refresh
    // while the edits sat in the browser's copy and the file had never seen them
    // — and the next Open then wrote the file straight over them. The stamp is
    // what survives a reload, so the stamp is what seeds this.
    initiallyDirty: holdsUnsavedWork(adoptedFileName()),
    save: async () => {
      if ((await persist({ auto: true })).where !== 'no-handle') return;
      // Same rule as a manual Save: a connected folder needs no question. The
      // folder's grant covers creating the file, so this works from a timer,
      // which has no user gesture of its own.
      const dir = await connectedFolder();
      if (dir) await saveIntoFolder(dir);
      else await autosaveHasNowhereToGo();
    },
    // A toast, so one line: the timer fires again, and the same cryptic
    // DOMException the manual Save used to show was landing here every tick
    // while some other program held the file.
    onError: (err) => api.ui.dialogs.toast(saveErrorSummary(err, adoptedFileName()), { kind: 'error' }),
    onDirtyChange: () => refreshSaveButton(),
  });

  /** A new file in the granted folder. Null only if the folder stopped working. */
  async function fresh(dir: FileSystemDirectoryHandle, name: string): Promise<EdbTarget | null> {
    const handle = await fileInFolder(dir, name, true);
    return handle ? { handle, name } : null;
  }

  /**
   * Save this workspace into the connected folder as `<workspace-id>.edb`.
   *
   * No name prompt in the ordinary case. A workspace id and its file name are one
   * convention (`spaceFileName`), the folder grant already covers writing in it,
   * and being asked to name a file whose name is a foregone conclusion is the sort
   * of dialog that makes Save feel like paperwork.
   *
   * A name already in the folder is the one exception, and it is confirmed rather
   * than dodged: that file may hold work from another machine, and this Save writes
   * the WHOLE open database over it. Offering another NAME instead — which is what
   * this did — only moved the workspace into a file whose name denies it.
   */
  /**
   * The workspace here against the file about to be replaced, one line each.
   *
   * A Save that has to overwrite a file is a conflict, and it was asked about with
   * a file NAME alone. The name is the one thing the two copies always share — the
   * file is named after the workspace — so it told the reader nothing, and the
   * answer throws away whichever copy is not kept. **The date the file was last
   * written is what decides it**: a file written an hour ago by another machine is
   * not the same proposition as one this tab wrote this morning.
   */
  /**
   * The two answers to "this file already holds this workspace".
   *
   * Each names the copy that SURVIVES, not the mechanism. "Replace it?" made the
   * reader work out which side "it" was, and "Overwrite" never said what was being
   * overwritten. The strings themselves are shared with the Open-workspace
   * question and the folder sync (`copy-choice.ts`): this used to say "disk" and
   * "local" where the sync said "disk version" and the open question said "the
   * copy in this browser", which is three vocabularies for two things.
   */
  const USE_DISK = USE_FILE_COPY;
  const USE_LOCAL = USE_BROWSER_COPY;

  /**
   * How many tables and views the FILE holds for this workspace.
   *
   * Size and date alone made a lopsided question: the browser's side was counted
   * in tables and the file's in kilobytes, so the two sides could not be compared
   * at all — and the guard against keeping an empty copy over a full one cannot
   * fire on a count it does not have.
   *
   * The whole file is read and opened in a throwaway database, which is what
   * `peekWorkspaces` costs. That is affordable exactly here: once, on a click,
   * behind a question the user has to stop and answer. The drop-a-file path pays
   * the same price for the same reason.
   *
   * Empty when the file cannot be read or holds no record for this workspace — a
   * conflict prompt is not the place to fail, and size and date still say
   * something.
   */
  async function countsInFile(handle: FileSystemFileHandle, workspaceId: string): Promise<CopyFacts> {
    const live = edbBridge();
    if (!live) return {};
    try {
      const bytes = new Uint8Array(await (await handle.getFile()).arrayBuffer());
      const mine = workspaceDocs(await live.peekWorkspaces(bytes)).find((w) => w.id === workspaceId);
      return mine ? { tables: mine.tables, views: mine.views } : {};
    } catch {
      return {};
    }
  }

  async function sidesAgainstFile(dir: FileSystemDirectoryHandle, file: string, workspaceId: string): Promise<{ text: string; here: CopyFacts; there: CopyFacts }> {
    const handle = await fileInFolder(dir, file, false);
    let here: CopyFacts = {};
    try {
      const c = await countWorkspaceContents(storeBridge(), workspaceId, { countRows: false });
      here = { tables: c.tables, views: c.views };
    } catch {
      /* a build that cannot count — the file's own side is the half that matters */
    }
    // When this browser's copy last moved away from that file. Without it the
    // browser line carried no date at all while the file line did, so the question
    // the reader is asking — which copy is later — was answerable on one side only.
    const changedAt = readStamp(file)?.changedAt;
    if (changedAt !== undefined) here.changedAt = changedAt;
    const facts = handle ? await factsOfHandle(handle) : null;
    const there: CopyFacts = { ...(facts ?? {}), ...(handle ? await countsInFile(handle, workspaceId) : {}) };
    // Usually `unknown` — a Save into a folder meets files this browser has never
    // read — and then neither side is marked. Where there IS a stamp, the marker
    // is the one fact the counts cannot carry.
    const newer = newerSide(compareWithFile(readStamp(file), facts));
    return {
      text: compareCopies([
        { label: BROWSER_SIDE, facts: here, newer: newer === 'browser' },
        { label: fileSide(file), facts: there, newer: newer === 'file' },
      ]),
      here,
      there,
    };
  }

  /**
   * Save found a file already carrying this workspace. Which copy wins?
   *
   * It used to be a yes/no — "Replace it with the workspace open here?" — and the
   * two answers were not the two outcomes. Yes overwrote the file; No did NOT keep
   * the file's copy, it simply abandoned the Save, so the one thing a reader most
   * likely wanted ("the disk has the newer copy, take that") was not on offer at
   * all and the question read as if it were.
   *
   * Both outcomes are named now, and each says which copy SURVIVES rather than
   * what happens mechanically. Dismissing still does nothing, which is what the
   * old No actually did.
   *
   * Returns true when the caller should go on and write the file.
   */
  async function settleSaveClash(dir: FileSystemDirectoryHandle, name: string, workspaceId: string): Promise<boolean> {
    const sides = await sidesAgainstFile(dir, name, workspaceId);
    const answer = await api.ui.dialogs.choice(`"${name}" is already in "${dir.name}". The two copies may differ — which one do you want to keep?${sides.text}`, [USE_DISK, USE_LOCAL], 'Save');
    if (answer === USE_DISK) {
      // Adopting IS the save: the tab switches to that database, so there is
      // nothing left to write out. The same branch the folder sync takes.
      if (!(await confirmDataLoss(api.ui.dialogs, name, sides.there, sides.here, 'the browser copy', 'Save'))) return false;
      await adoptFolderFile(workspaceId, name);
      return false;
    }
    if (answer !== USE_LOCAL) return false; // dismissed: neither copy is touched
    return (await confirmDataLoss(api.ui.dialogs, name, sides.here, sides.there, `the file copy`, 'Save')) === true;
  }

  async function saveIntoFolder(dir: FileSystemDirectoryHandle): Promise<void> {
    const workspaceId = api.workspaceId();
    // Unreachable from the menu or the timer — boot resolves a workspace before
    // either exists — but the file has to be named after something.
    if (!workspaceId) return;
    const name = spaceFileName(workspaceId);
    const taken = (await listWorkspaceFiles(dir)).includes(name);
    if (taken && !(await settleSaveClash(dir, name, workspaceId))) return;
    const target = await fresh(dir, name);
    if (!target?.handle) return;
    setEdbHandle(target.handle);
    await rememberHandle(target.handle);
    setActiveEdbName(target.name);
    if (!(await save())) return;
    // The marker now names a file this browser's POOL has never heard of, and boot
    // reads the pool — never the user's file (see `session.ts`). So without this the
    // next load opened a database that did not exist, the pool obligingly made it
    // empty, and the workspace came up with no tables: safe in the 48 KB file on
    // disk, gone from the screen, and the boot then re-created the workspace record
    // on top. Every other adopt already does this (`adoptFolderFile`, `open`); this
    // was the one route that changed the name without placing the bytes.
    //
    // Read back from the file rather than exported again, so the pool's copy is the
    // bytes that are actually on disk.
    //
    // `adopt`, because this Save does not reload: the marker now names
    // `target.name` and the worker still holds the database it booted on, so
    // without it every edit until the next load was written to a database that
    // load does not open — the work simply gone, with no error and no dot. The
    // worker follows the marker here instead.
    await placeForNextBoot(target.name, await readBytes(target.handle), { adopt: true });
  }

  /**
   * A manual Save with no file behind it.
   *
   * Save always means "write to the user's disk", so the only question is where —
   * and the answer is a folder, never a lone file: a folder grant covers every
   * later save and every other workspace, where a per-file grant has to be
   * re-obtained from a gesture the autosave timer does not have.
   *
   * Runs from a click, so the picker has the user gesture it needs.
   */
  async function askForSaveTarget(): Promise<void> {
    if (!canPickFolder()) {
      await api.ui.dialogs.alert(NO_FILE_ACCESS, 'Save');
      return;
    }
    // A dismissed dialog is a cancelled Save, which writes nothing.
    const answer = await api.ui.dialogs.choice(NO_FOLDER_YET, [CONNECT_FOLDER], 'Save');
    if (answer !== CONNECT_FOLDER) return;
    const picked = await pickFolder();
    if (!picked) return;
    // Remembered, but NOT scanned: the user asked to save, not to rebuild the
    // workspace list.
    await connectFolder(picked);
    await saveIntoFolder(picked);
  }

  /**
   * The same question, asked by the autosave timer instead of by a click.
   *
   * A timer has no user gesture, so this cannot open a picker directly — but the
   * dialog's own button is a gesture, which is what makes *Connect a folder*
   * work from here at all.
   *
   * Every other answer turns autosave OFF, including a dismissed dialog. A modal
   * that reopens on the next debounce tick is a trap, and the flag below closes
   * the remaining window where two ticks could both be waiting on an answer.
   */
  let askingAboutAutosave = false;
  async function autosaveHasNowhereToGo(): Promise<void> {
    if (askingAboutAutosave) return;
    askingAboutAutosave = true;
    try {
      const answer = await api.ui.dialogs.choice('Autosave has no folder to write to.', [...(canPickFolder() ? [CONNECT_FOLDER] : []), TURN_OFF_AUTOSAVE], 'Autosave');
      if (answer === CONNECT_FOLDER && canPickFolder()) {
        const picked = await pickFolder();
        if (picked) {
          await connectFolder(picked);
          await saveIntoFolder(picked);
          return;
        }
      }
      autosave.setEnabled(false);
      await api.settings.set(meta.id, AUTOSAVE_KEY, false, 'user');
      await refreshFileCommands();
      api.ui.dialogs.toast('Autosave is off until this workspace has a file.', { kind: 'info' });
    } finally {
      askingAboutAutosave = false;
    }
  }

  // Every write the worker reports marks the file unsaved. This is the store's
  // own change broadcast, so nothing has to remember to announce itself.
  edbBridge()?.onChanged(() => autosave.changed());

  // Separately, and NOT for every collection: written down, because a sync or a
  // `?space=` switch decides whether the copy of a file on disk may replace this
  // one, and both can run at boot, where the policy above does not exist yet.
  //
  // `settings` and `plugins` are left out on purpose. Running ANY command through
  // the palette upserts the recent-command list into `settings`, so counting that
  // as unsaved work would make "Sync workspace folder" report a conflict with
  // itself, every time. The cost is that a settings-only difference loses to the
  // file — the price of the file ever being able to win.
  edbBridge()?.onChanged((coll) => {
    if (coll === 'settings' || coll === 'plugins') return;
    markLocalChanges(activeEdbName());
  });

  // The worker's own warnings — a failed crash-recovery mirror, above all.
  edbBridge()?.onWarning((message) => api.ui.dialogs.toast(message, { kind: 'warning' }));

  // An import is many calls, and each one would otherwise arm the timer. Holding
  // the batch open means one save at the end instead of one per chunk.
  api.events.on('import:before', () => autosave.beginBatch());
  api.events.on('import:after', () => autosave.endBatch());

  /**
   * A dropped `.edb` brings its workspace INTO this browser.
   *
   * Not an Open: a drop must not repoint the tab at somebody's file and start
   * saving into it. The file is read and left alone, and what arrives is an
   * ordinary workspace of this browser's own — the same end state as New
   * workspace, with data in it.
   *
   * A `.edb` holds ONE workspace, so there is normally nothing to choose: the
   * file's own name says which (`northwind.edb` → `northwind`), and that is the
   * invariant Save and Open are built on (`spaceFileName`).
   *
   * The two fallbacks are for a file that BREAKS the rule, not for a shape this
   * app produces: one written before v0.0.427, when Save wrote the whole database
   * into one file, or one whose name the user changed in an OS save dialog. Then
   * the only workspace in it wins, and failing that the user is asked. Do not read
   * the picker as permission to write such a file.
   */
  async function importDroppedFile(file: File): Promise<void> {
    const live = edbBridge();
    if (!live) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const inside = workspaceDocs(await live.peekWorkspaces(bytes));
    if (inside.length === 0) {
      await api.ui.dialogs.alert(`There is no easyDBAccess workspace in "${file.name}".`, 'Open workspace file');
      return;
    }
    const named = workspaceIdFromFileName(file.name);
    const source = pickWorkspaceIn(inside, named) ?? (await askWhichWorkspace(inside));
    if (!source) return;

    const taken = new Set((await api.store.workspaces.find()).map((w) => w.id));
    let target = source.id;
    let mode: 'fresh' | 'overwrite' | 'rename' = 'fresh';
    if (taken.has(source.id)) {
      const answer = await api.ui.dialogs.choice(
        `"${workspaceLabel(source)}" is already a workspace here. Replace what is in this browser with the copy from "${file.name}", or keep both?${await bothCopies(source, file)}`,
        [OVERWRITE_LOCAL, KEEP_BOTH],
        'Open workspace file',
      );
      if (!answer) return;
      mode = answer === OVERWRITE_LOCAL ? 'overwrite' : 'rename';
      if (mode === 'rename') target = freeWorkspaceId(source.id, taken);
    }
    await bringWorkspaceIn(bytes, file.name, source, target, mode);
  }

  /**
   * The two copies, side by side, for the replace-or-keep-both question.
   *
   * Without it the question is about a name only, and both copies have the same
   * name — so "replace" was a decision taken blind, and it is the one that throws
   * work away. What is counted here is the workspace, and the dropped file's size
   * and date are the file's; a count this side cannot take is left out rather than
   * shown as zero.
   */
  async function bothCopies(source: WorkspaceDoc, file: File): Promise<string> {
    let here = {};
    try {
      const c = await countWorkspaceContents(storeBridge(), source.id, { countRows: false });
      here = { tables: c.tables, views: c.views };
    } catch {
      /* a build that cannot count — the file's side is still worth showing */
    }
    return compareCopies([
      { label: BROWSER_SIDE, facts: here },
      { label: fileSide(file.name), facts: { tables: source.tables, views: source.views, size: file.size, mtime: file.lastModified } },
    ]);
  }

  /** The workspace a file name points at, or the only one in the file. */
  function pickWorkspaceIn(inside: readonly WorkspaceDoc[], named: string): WorkspaceDoc | null {
    return inside.find((w) => w.id === named) ?? (inside.length === 1 ? inside[0]! : null);
  }

  /**
   * A file that holds several workspaces and is not named after any of them.
   *
   * Only reachable for a file this app should not have written — see
   * `importDroppedFile`. The question says so, rather than presenting a broken
   * file as an ordinary choice.
   */
  async function askWhichWorkspace(inside: readonly WorkspaceDoc[]): Promise<WorkspaceDoc | null> {
    // Labels, then matched back by POSITION. Two workspaces in one file may carry
    // the same title — that is ordinary now that a title is free text — and a
    // `find` on the answer would always return the first of them.
    const labels = listLabels(inside);
    const pick = await api.ui.dialogs.choice('This file holds more than one workspace, which a workspace file is not supposed to. Which one should come in?', labels, 'Open workspace file');
    if (!pick) return null;
    return inside[labels.indexOf(pick)] ?? null;
  }

  /**
   * Copy `source` out of the dropped bytes and into this browser as `target`.
   *
   * The bytes go into a THROWAWAY worker, which lands on the memory substrate
   * because the pool is exclusive to the live session — the same arrangement
   * `overwriteInFile` and `peekWorkspaces` rely on.
   *
   * A rename is done INSIDE that throwaway (`cloneWorkspace`) rather than while
   * copying: `copyWorkspace` writes each document under the id it already carries,
   * so the id has to be settled before anything crosses over.
   */
  async function bringWorkspaceIn(bytes: Uint8Array, fileName: string, source: WorkspaceDoc, target: string, mode: 'fresh' | 'overwrite' | 'rename'): Promise<void> {
    // BEFORE anything is deleted. `overwrite` removes the local copy and then
    // writes the file's one over it, and the write can be refused: the open
    // database may be a `.edb` named after a different workspace, which the store
    // will not take a second one into. Finding that out after the delete would
    // leave the user with neither copy — the alert below would explain a loss
    // that had already happened.
    if (!mayCreateWorkspaceIn(activeEdbName(), target)) {
      await api.ui.dialogs.alert(
        `"${target}" cannot be opened into this workspace file. ${activeEdbName()} holds one workspace, and it is not that one — switch to the project index first, or open ${fileName} directly.`,
        'Open workspace file',
      );
      return;
    }
    const scratch = createEdbBridge();
    setAppProgress({ label: `Opening ${fileName}` });
    // The batch directly rather than through `import:before`: that event carries a
    // table id and a row count, and this is a whole workspace. The effect wanted is
    // the same — one save at the end, not one per chunk.
    autosave.beginBatch();
    try {
      await scratch.open(bytes, DROP_SCRATCH);
      if (mode === 'overwrite') await deleteWorkspace(storeBridge(), target);
      if (mode === 'rename') await cloneWorkspace(scratch, { from: source.id, to: target, title: source.title, mode: 'all' });
      const from = createIpcDataStore(scratch, () => target);
      const to = createIpcDataStore(storeBridge(), () => target);
      const result = await copyWorkspace(from, to, target, (p) => setAppProgress({ label: `Opening ${fileName}`, detail: p.label }));
      api.ui.dialogs.toast(`"${target}" is now a workspace here: ${result.tables} table(s), ${result.rows} row(s).`, { kind: 'success' });
    } catch (err) {
      await api.ui.dialogs.alert(`"${fileName}" could not be opened: ${err instanceof Error ? err.message : String(err)}`, 'Open workspace file');
      return;
    } finally {
      autosave.endBatch();
      clearAppProgress();
      scratch.terminate();
    }
    // Switch to what just arrived. A reload, because the store, the panels and the
    // plugin host all bind to one workspace at boot.
    reloadWithSpace(target);
  }

  api.ui.registerDropHandler(async (event) => {
    const dropped = filesFrom(event).filter((f) => f.name.toLowerCase().endsWith(EDB_EXTENSION));
    if (dropped.length === 0) return false;
    event.preventDefault();
    // One file per drop: each one asks its own questions and ends in a reload, so
    // a second would be answering into a page that is going away.
    if (dropped.length > 1) {
      api.ui.dialogs.toast('One workspace file at a time, please.', { kind: 'info' });
      return true;
    }
    await importDroppedFile(dropped[0]!);
    return true;
  });

  /**
   * Write the workspace to its file.
   *
   * Answers whether it actually wrote, which one caller needs: the Save that has
   * just pointed this tab at a NEW file has to put those bytes in the pool too,
   * and must not do it for a save that failed. See {@link saveIntoFolder}.
   */
  async function save(): Promise<boolean> {
    let result: SaveResult;
    try {
      result = await persist();
    } catch (err) {
      // Not a toast: a message about a save that did not happen must not vanish
      // after seven seconds. Everything saved before this is still intact — the
      // workspace itself is in SQLite and was never in the way.
      //
      // Through `saveErrorMessage`, because the common failures arrive as
      // DOMExceptions whose own wording describes the API's cached state rather
      // than the file being busy — see that module.
      await api.ui.dialogs.alert(saveErrorMessage(err, adoptedFileName()), 'Save');
      return false;
    }
    if (result.where === 'no-handle') {
      // A connected folder is already an answer to "where does this go?", so the
      // first Save after connecting one asks nothing. Either route ends in
      // `saveIntoFolder`, which reports its own result — this call did not write.
      const dir = await connectedFolder();
      if (dir) await saveIntoFolder(dir);
      else await askForSaveTarget();
      return false;
    }
    if (result.where === 'none') return false;
    autosave.markClean();
    // Names the file. "Workspace saved" left the one question a save raises
    // unanswered — saved WHERE — and with a workspace folder in play the answer is
    // not obvious: the file is named after the workspace, not after what the user
    // last picked in a dialog.
    //
    // Any file written for a workspace that had none is named too. A Save that
    // quietly produced four files nobody asked for would be the more surprising of
    // the two, and the note only appears on the save that produces them.
    api.ui.dialogs.toast(`Workspace saved to ${activeEdbName()}.${alsoWroteNote(result.alsoWrote)}`, { kind: 'success' });
    return true;
  }

  /**
   * Write the open workspace out over the file's copy of it.
   *
   * The file's OTHER workspaces survive — which is DAMAGE LIMITATION on a file
   * that is already wrong, not the shape a workspace file is meant to have. A
   * `.edb` holds one workspace (`one-per-file.ts`); one holding several was written
   * before v0.0.427, and a passenger in a file this tab never adopted may have no
   * other copy anywhere, so this is not the place to delete it. Its bytes go into a
   * throwaway worker, that worker's copy of the workspace is dropped, the open one
   * is copied in over the top, and the result is written back.
   *
   * The throwaway runs on the MEMORY substrate, not the pool: the pool's files are
   * exclusive origin-wide and the live session already holds it, so a second
   * worker in this tab cannot install one. `buildEdbFile` relies on the same
   * thing. It writes no mirror at all, so there is nothing to keep apart.
   */
  async function overwriteInFile(dir: FileSystemDirectoryHandle, workspaceId: string, file: string): Promise<void> {
    const handle = await fileInFolder(dir, file, false);
    if (!handle || !(await ensureWritable(handle, true))) {
      await api.ui.dialogs.alert(`easyDBAccess may not write ${file}.`, 'Sync workspace folder');
      return;
    }
    const scratch = createEdbBridge();
    try {
      await scratch.open(await readBytes(handle), SYNC_SCRATCH);
      // One id, because a clash is matched on the id now. This used to take the
      // file's id separately and delete both, since a NAME-matched clash could pair
      // two workspaces whose ids differed — and then the file had to be cleared of
      // each, or it would come out holding both.
      await scratch.deleteWorkspace?.(workspaceId);
      await copyWorkspace(
        api.store,
        createIpcDataStore(scratch, () => workspaceId),
        workspaceId,
      );
      if (!(await writeUserFile(handle, await scratch.export(), file, 'Overwrite the copy in a file'))) return;
      api.ui.dialogs.toast(`Wrote this workspace over the copy in ${file}.`, { kind: 'success' });
    } finally {
      scratch.terminate();
    }
  }

  /**
   * Rename the workspace inside somebody else's file, so its id matches the name.
   *
   * The repair for `b.edb` holding the workspace `a` — see `db/edb/file-identity.ts`
   * for why that file is otherwise unreachable. Same throwaway-worker arrangement as
   * `overwriteInFile`: the pool is exclusive origin-wide, so a second worker in this
   * tab lands on the memory substrate, which is what makes reading and rewriting a
   * foreign file possible at all.
   *
   * `cloneWorkspace` then a delete, rather than an id edit, because there is no such
   * edit: the id keys every table, view, template and setting in the file. The clone
   * carries all of it across and re-mints the ids of the copies, which is harmless —
   * those ids are private to this file.
   *
   * The `title` is carried over by hand. A clone writes a fresh workspace record
   * from `from`'s plugin list alone, so the display name a user chose would be lost
   * — and this is a rename, not a new workspace.
   */
  async function renameInFile(dir: FileSystemDirectoryHandle, file: string, from: string, to: string): Promise<boolean> {
    const handle = await fileInFolder(dir, file, false);
    if (!handle || !(await ensureWritable(handle, true))) {
      await api.ui.dialogs.alert(`easyDBAccess may not write ${file}, so the workspace in it cannot be renamed.`, 'Sync workspace folder');
      return false;
    }
    const scratch = createEdbBridge();
    try {
      await scratch.open(await readBytes(handle), SYNC_SCRATCH);
      // The title travels WITH the clone. It used to be read out and patched back
      // afterwards, because a clone carried only the id and the `name` that
      // followed it — there is no such field now, and the title is the one thing
      // a rename must not drop.
      const before = (await scratch.findOne('workspaces', from)) as { title?: unknown } | null;
      const title = typeof before?.title === 'string' && before.title ? before.title : '';
      await cloneWorkspace(scratch, { from, to, ...(title ? { title } : {}), mode: 'all' });
      await scratch.deleteWorkspace?.(from);
      if (!(await writeUserFile(handle, await scratch.export(), file, 'Rename the workspace in a file'))) return false;
      api.ui.dialogs.toast(`The workspace in ${file} is now "${to}".`, { kind: 'success' });
      return true;
    } catch (err) {
      await api.ui.dialogs.alert(`The workspace in ${file} could not be renamed: ${err instanceof Error ? err.message : String(err)}`, 'Sync workspace folder');
      return false;
    } finally {
      scratch.terminate();
    }
  }

  /**
   * Connect a folder and rebuild the workspace list from every `.edb` in it.
   *
   * Connecting IS the sync — a folder the app knows about but has not read is a
   * list that silently omits workspaces the user can see on disk.
   */
  async function chooseFolder(): Promise<void> {
    const picked = await pickFolder();
    if (!picked) return;
    // "Connect" becomes "Change" from here on.
    await connectFolder(picked);
    await syncFolderNow(picked);
  }

  /**
   * Give the folder grant back.
   *
   * Only the grant and the cached index go: every `.edb` stays exactly where it
   * is, which is what the confirm has to say, because "disconnect" beside a
   * list of files reads like a delete. The per-file selection is left alone on
   * purpose — reconnecting the same folder should come back to the same
   * choices, not silently switch everything on again.
   */
  async function disconnectFolder(): Promise<void> {
    if ((await rememberedFolder()) === null) return;
    if (!(await api.ui.dialogs.confirm('Stop using this folder? Every file in it stays where it is — this app just forgets it may read them.', 'Local Data'))) return;
    await forgetFolder();
    // Stop mirroring settings into it as well, or every later write is still
    // coalesced into a file in the folder the app was just told to forget — and
    // once the grant lapses that write starts failing with nothing on screen.
    disconnectDeviceState();
    clearFolderIndex();
    window.dispatchEvent(new CustomEvent('easydb:folder-index-changed'));
    await refreshFileCommands();
    api.ui.dialogs.toast('Folder disconnected. The files are untouched.', { kind: 'info', title: 'Local Data' });
  }

  /**
   * Re-read the connected folder. Offered separately, because files change on disk.
   *
   * Says so rather than opening a picker when there is no folder: a palette is a
   * flat list of everything, so a command has to answer for the state it is run
   * in — and "connect a folder" is the command next to this one.
   */
  async function syncConnectedFolder(): Promise<void> {
    if ((await rememberedFolder()) === null) {
      api.ui.dialogs.toast('No workspace folder is connected yet.', { kind: 'info' });
      return;
    }
    const dir = await workspaceFolder();
    if (!dir) return;
    await syncFolderNow(dir);
  }

  /**
   * Sync THIS workspace with its own `.edb` — the narrow half of the command
   * above.
   *
   * `Sync workspace folder` re-reads every enabled file in the folder to settle
   * the workspace LIST. That is the right job when files have been added or
   * moved, and the wrong one for the ordinary question: another machine wrote
   * MY file, what now. This opens that one file and nothing else.
   *
   * Every outcome is said out loud, including the two the folder report leaves
   * silent. `describeActiveOutcome` returns an empty clause for `no-file` and
   * `missing` on purpose — inside a folder report they are noise about
   * something the user did not ask about — but here they ARE the answer, and a
   * command that ran and said nothing is the bug this whole area already fixed
   * once.
   */
  async function syncActiveWorkspaceNow(): Promise<void> {
    if ((await rememberedFolder()) === null) {
      api.ui.dialogs.toast('No workspace folder is connected yet.', { kind: 'info', title: 'Sync workspace' });
      return;
    }
    const dir = await workspaceFolder();
    if (!dir) return;
    const { outcome, file } = await syncActiveWorkspace(
      dir,
      api.store,
      api.ui.dialogs,
      (workspaceId, name) => overwriteInFile(dir, workspaceId, name),
      async (handle, mode) => {
        const ctx = mergeContext(handle);
        return ctx ? (await mergeWithFile(ctx, mode)).merged : false;
      },
    );

    // This tab is on the browser's own database, so there is no file to sync
    // with. Named as the next step rather than as a refusal: a Save into the
    // connected folder is what gives this workspace a file.
    if (outcome === 'no-file') {
      api.ui.dialogs.toast('This workspace has no file yet. Save it into the folder first.', { kind: 'info', title: 'Sync workspace' });
      return;
    }
    if (outcome === 'missing') {
      api.ui.dialogs.toast(`${file ?? 'That file'} is not in this folder.`, { kind: 'warning', title: 'Sync workspace' });
      return;
    }
    // `loaded` ends in a reload, so this toast is the last thing this page does.
    api.ui.dialogs.toast(describeActiveOutcome(outcome, file ?? '').trim(), { kind: 'success', title: 'Sync workspace' });
  }

  async function syncFolderNow(dir: FileSystemDirectoryHandle): Promise<void> {
    const report = await syncFolder(
      dir,
      api.store,
      api.ui.dialogs,
      (workspaceId, file) => overwriteInFile(dir, workspaceId, file),
      // The other half of the sync: a workspace this browser holds and the folder
      // does not gets written out, so connecting a folder leaves nothing behind.
      // Empty ones are skipped — `?space=x` makes a shell before any folder is
      // connected, and a folder full of empty files is not a sync, it is litter.
      // The open workspace included: it is the one the user can see, so leaving
      // it as the only workspace with no file is the opposite of what they asked
      // for. Written, not adopted — see the note in `fileTheStranded`.
      () => fileTheStranded({ skipEmpty: true, includeActive: true }),
      (file, from, to) => renameInFile(dir, file, from, to),
      // Settling this tab's own file table by table, for the two states where
      // both copies may hold work. Passed in rather than reached for, because
      // `folder-sync.ts` has neither the bridge nor the workspace id.
      async (handle, mode) => {
        const ctx = mergeContext(handle);
        return ctx ? (await mergeWithFile(ctx, mode)).merged : false;
      },
    );
    const skipped = report.unreadable.length > 0 ? ` ${report.unreadable.length} file(s) held no workspace.` : '';
    // Files this device has switched off. Said out loud for the same reason the
    // misfiled ones are: a count that is lower than the folder looks is the
    // report that sends people looking for a bug.
    const off = report.offFiles > 0 ? ` ${report.offFiles} file(s) switched off in Local Data.` : '';
    const written = alsoWroteNote(report.wrote);
    // A file whose name denies the workspace inside it has already had its own
    // dialog, so this only counts them — but it must count them, because a sync
    // that quietly listed fewer workspaces than the folder holds is the report
    // that sent us looking in the first place.
    const misfiled = report.ignored.length > 0 ? ` ${report.ignored.join(', ')} left out: the name does not match the workspace inside.` : '';
    const fixed = report.renamed.length > 0 ? ` Renamed the workspace in ${report.renamed.join(', ')}.` : '';
    // A file holding several workspaces. Said rather than asked: it is still listed
    // and still works, and the repair is the ordinary Save, which only the tab that
    // has that file open can do. Two rows out of one file are indistinguishable in
    // the selector otherwise, which is how this was found.
    const shared = report.shared.length > 0 ? ` ${report.shared.join(', ')} holds more than one workspace; a workspace file holds one. Open it and save to give each its own file.` : '';
    // What happened to THIS tab's file, in the same breath. Without it the toast
    // read the same whether the sync had loaded the file or decided it could not,
    // which is what "Sync does nothing" looked like from the outside.
    const own = describeActiveOutcome(report.active, report.activeFile ?? '');
    api.ui.dialogs.toast(`"${dir.name}": ${report.found} workspace(s) in ${report.files} file(s).${off}${skipped}${fixed}${misfiled}${shared}${written}${own}`, { kind: 'success' });
    // The selector reads the index once, on connect, so it has to be told.
    window.dispatchEvent(new CustomEvent('easydb:folder-index-changed'));
  }

  /**
   * Make this tab use `name` from the next load on, and reload into the workspace
   * that file is named after.
   *
   * The store is built once per load, so adopting another file is a reload — the
   * same thing the desktop does when it opens another database.
   *
   * `a.edb` is the workspace `a` (`workspaceIdFromFileName`), and the reload asks
   * for it by name. The old `?space=` from whatever was open before must not
   * survive; dropping it and letting boot guess is what made Open land in
   * `default`, or in whichever workspace the file returned first.
   */
  async function adopt(target: EdbTarget, message: string): Promise<void> {
    await adoptEdbFile(target);
    await api.ui.dialogs.alert(message, 'Workspace file');
    reloadWithSpace(workspaceIdFromFileName(target.name));
  }

  /**
   * Refuse a file whose one workspace is not the one its name says.
   *
   * Open reads the workspace out of the FILE NAME and reloads asking for it, so
   * `beta.edb` holding `alpha` used to open, find no `beta`, and create an empty one
   * inside the file — leaving `beta.edb` holding two workspaces and the data the
   * user came for out of sight. The same bug as two files sharing an id, reached
   * through a different door, so it gets the same answer: do not open it.
   *
   * Only for a file holding exactly ONE workspace. A file holding several is the
   * pre-v0.0.427 shape, which Open already copes with by name, and an unreadable
   * file has its own message further down.
   */
  async function nameDeniesContents(name: string, bytes: Uint8Array): Promise<boolean> {
    const live = edbBridge();
    if (!live) return false;
    const inside = workspaceDocs(await live.peekWorkspaces(bytes));
    const only = inside.length === 1 ? inside[0]! : null;
    const wanted = workspaceIdFromFileName(name);
    if (!only || only.id === wanted) return false;
    await api.ui.dialogs.alert(
      `"${name}" holds the workspace "${only.id}", not "${wanted}". Opening it would make an empty "${wanted}" beside it. Run "Sync workspace folder" to rename the workspace inside the file, or rename the file itself.`,
      'Open workspace',
    );
    return true;
  }

  /**
   * Open a workspace file.
   *
   * The folder is listed first, so the ordinary case is picking a name out of a
   * list this app already has permission to read. The OS dialog is only reached
   * by asking for it, or where there is no folder.
   */
  async function open(): Promise<void> {
    const dir = await workspaceFolder();
    let picked: { name: string; bytes: Uint8Array; handle: FileSystemFileHandle | null } | null = null;
    if (dir) {
      const files = await listWorkspaceFiles(dir);
      if (files.length > 0) {
        const answer = await api.ui.dialogs.choice(`Workspace files in "${dir.name}"`, [...files, BROWSE], 'Open workspace');
        if (answer === null) return;
        if (answer !== BROWSE) {
          const handle = await fileInFolder(dir, answer, false);
          if (handle) picked = { name: answer, bytes: await readBytes(handle), handle };
        }
      }
    }
    picked ??= await pickFileToOpen();
    if (!picked) return;
    if (await nameDeniesContents(picked.name, picked.bytes)) return;
    const workspaceId = workspaceIdFromFileName(picked.name);
    if (!(await mayImportOver(picked.name, picked.handle, workspaceId))) return;
    // The boot never reads the user's file (see `session.ts`), so the bytes go
    // into this tab's own substrate first, and the reload finds them there.
    await placeForNextBoot(picked.name, picked.bytes);
    // This copy came straight out of that file, so the two agree.
    const opened = picked.handle ? await factsOfHandle(picked.handle) : null;
    if (opened) recordAgreement(picked.name, opened);
    await adopt({ name: picked.name, handle: picked.handle }, `Opening "${picked.name}" as the workspace "${workspaceId}". The page will reload.`);
  }

  /**
   * May this Open write the file's bytes over the copy this browser holds?
   *
   * Open imports, and importing replaces. The copy being replaced may hold work
   * the file has never seen — the ordinary state after editing a workspace and
   * not saving it — and this route took it away without a word. Every OTHER path
   * that can replace a copy asks first: the workspace selector and the boot both
   * go through `settleTwoCopies`, and the Sync through `decideActiveFileSync`.
   *
   * Answers false when the Open is finished — either the user dismissed the
   * question, or they kept the browser's copy, which is an Open in its own right:
   * the tab ends up backed by that file, so a Save or a Compare has somewhere to
   * go, and nothing was imported.
   *
   * Only `ahead` and `conflict` ask. Those two are the verdicts that PROVE we
   * hold unsaved work — the stamp says so, written by every change since the last
   * agreement. `unknown` proves nothing, and the user has just named this file by
   * hand, so a question there would be a modal in front of every ordinary Open of
   * a file first read on another origin.
   */
  async function mayImportOver(file: string, handle: FileSystemFileHandle | null, workspaceId: string): Promise<boolean> {
    const verdict = await verdictFor(file, handle);
    if (verdict !== 'ahead' && verdict !== 'conflict') return true;
    const answer = await settleCopyQuestion(api.ui.dialogs, file, workspaceId, verdict);
    if (answer === 'none') return false;
    if (answer === 'file') return true;
    // Comparing opens the browser's copy first — the side that destroys nothing —
    // and the comparison then settles the two against the file. Same order as the
    // workspace selector's answer to the same question.
    if (answer === 'compare') askForComparison(file);
    await adopt({ name: file, handle }, `Opening "${file}" as the workspace "${workspaceId}", keeping the copy in this browser. The page will reload.`);
    return false;
  }

  async function leaveFileMode(): Promise<void> {
    // Reachable with no file behind it now that this is a command rather than a
    // menu item the file mode could hide.
    if (adoptedFileName() === null) {
      api.ui.dialogs.toast('This workspace is not in a file \u2014 there is nothing to come back from.', { kind: 'info' });
      return;
    }
    if (!(await api.ui.dialogs.confirm('Go back to this browser\u2019s own database? The file stays where it is.', 'Local database'))) return;
    await forgetHandle();
    setActiveEdbName(null);
    reloadWithoutSpace();
  }

  /**
   * The header's Save button, with the unsaved marker on its label.
   *
   * A `ButtonSpec` is static and the shell renders from a snapshot of the registry,
   * so the label is edited in place and the shell is asked to re-render. The
   * alternative — a live-updating button type in the plugin API — would be a new
   * contract for every plugin to support one dot.
   *
   * Not registered where the browser cannot produce a file at all: a permanent
   * button whose only answer is "this browser cannot save" is worse than no button.
   */
  const saveButton: ButtonSpec = {
    id: 'edb-file:save',
    label: 'Save',
    icon: 'save',
    tooltip: 'Save this workspace to its file',
    variant: 'primary',
    onClick: () => void save(),
  };

  function refreshSaveButton(): void {
    const dirty = autosave.isDirty();
    // The red dot in the button's corner, drawn by the shell — the notification
    // convention, and the only marker that reads at a glance without changing the
    // button's width as the label did.
    saveButton.badge = dirty;
    saveButton.tooltip = dirty ? 'Unsaved changes — click to write them to the file' : 'Everything here is saved';
    document.dispatchEvent(new CustomEvent('easydb:refresh-buttons'));
  }

  if (canReachAFile()) api.ui.registerHeaderButton(saveButton);

  async function toggleAutosave(): Promise<void> {
    const next = !autosave.enabled();
    autosave.setEnabled(next);
    await api.settings.set(meta.id, AUTOSAVE_KEY, next, 'user');
    await refreshFileCommands();
    api.ui.dialogs.toast(`Autosave ${next ? 'on' : 'off'}`, { kind: 'info' });
  }

  /**
   * Autosave is a SETTING as well as a command, and both drive the one record.
   *
   * The Settings dialog writes straight to the store, so without this the tick
   * box would change what a RELOAD does and nothing else: the timer would stay
   * as it was, and the palette would go on offering to turn on what the setting
   * already said was on. That is the same trap the stored flag fell into before
   * v0.0.443 — see the note on `session` at the top of this file.
   *
   * Reading the record back rather than trusting the event is deliberate: the
   * event carries no value (`db/settings-events.ts`), which is what keeps one
   * source of truth.
   */
  document.addEventListener(SETTINGS_CHANGED_EVENT, (e) => {
    const detail = (e as CustomEvent<SettingsChangedDetail>).detail;
    if (!settingsChangeAffects(detail, meta.id, AUTOSAVE_KEY)) return;
    void (async () => {
      const on = (await api.settings.get(meta.id, AUTOSAVE_KEY)) === true;
      if (on === autosave.enabled()) return;
      autosave.setEnabled(on);
      await refreshFileCommands();
    })();
  });

  /**
   * Two of the commands say what they will do, so their titles follow the state.
   *
   * Edited in place, with nothing told: the palette rebuilds its list on every
   * open and reads these very objects, so the next open is already right. Called
   * at boot, and again wherever the state behind a title changes.
   *
   * `rememberedFolder` is a plain read of what was granted before — never
   * `ensureWritable`, because working out a title must not raise a permission
   * prompt.
   */
  async function refreshFileCommands(): Promise<void> {
    autosaveCommand.title = `${autosave.enabled() ? 'Turn off' : 'Turn on'} autosave`;
    folderCommand.title = (await rememberedFolder()) ? 'Change workspace folder…' : 'Connect workspace folder…';
  }

  const autosaveCommand: CommandSpec = {
    id: 'edb-file:autosave',
    title: 'Turn on autosave',
    group: FILE_GROUP,
    icon: 'autorenew',
    keywords: ['auto', 'save', 'timer', 'file'],
    run: () => toggleAutosave(),
  };

  const folderCommand: CommandSpec = {
    id: 'edb-file:folder',
    title: 'Connect workspace folder…',
    group: FILE_GROUP,
    icon: 'folder_special',
    keywords: ['change', 'connect', 'directory', 'file'],
    run: () => chooseFolder(),
  };

  // `compareWithOwnFile` goes in the holder for the same reason the autosave
  // policy does: `load()` is a separate export and cannot see this closure, and
  // both of the answers it has to carry out — a question boot left behind, and a
  // "Compare them…" that survived the reload — end in exactly this call.
  session = { autosave, refreshSaveButton, refreshFileCommands, compareWithOwnFile, ownFile };

  api.ui.registerCommand({
    id: 'edb-file:open',
    title: 'Open workspace file…',
    group: FILE_GROUP,
    icon: 'folder_open',
    keywords: ['edb', 'file', 'load', 'switch'],
    run: () => open(),
  });

  // The "Local Data" half of the Connect menu. A connector rather than another
  // header button: Connect already asks the user's question ("what data can
  // this app reach?"), and registering here is what stops `connect-menu.ts`
  // from having to know this plugin exists. See `dialogs/local-data-dialog.ts`.
  //
  // Registered even where the folder picker is missing, because the dialog
  // still offers the single-file open that every browser can do — and where it
  // cannot, it says so, which is better than a Connect menu with one half.
  api.ui.registerConnector({
    id: 'workspace-folder',
    scope: 'local',
    label: 'Local Data',
    icon: 'folder',
    order: 10,
    description: 'The folder your workspaces live in, and which files in it this device uses.',
    connect: async () => {
      openLocalDataDialog({
        chooseFolder,
        disconnectFolder,
        rescan: syncConnectedFolder,
        openFile: open,
      });
    },
  });

  // Save is a HEADER BUTTON, and the palette already lists every button under
  // "Actions" — so registering it as a command too would be the same entry twice.
  // Where the browser cannot produce a file the button is not there at all, and
  // then this is the only place left that can explain why.
  if (!canReachAFile()) {
    api.ui.registerCommand({
      id: 'edb-file:save',
      title: 'Save workspace to a file',
      group: FILE_GROUP,
      icon: 'save',
      keywords: ['write', 'disk', 'edb'],
      run: () => void save(),
    });
  }

  api.ui.registerCommand(autosaveCommand);

  if (canPickFolder()) {
    api.ui.registerCommand(folderCommand);
    // Listed BEFORE the folder sync, because it is the narrower of the two and
    // the one a user reaching for "sync" usually means: this workspace against
    // its own file. The titles are deliberately prefix-shared so both surface
    // on the same search, and the longer one says out loud that it is the
    // bigger job.
    api.ui.registerCommand({
      id: 'edb-file:sync-workspace',
      title: 'Sync workspace',
      group: FILE_GROUP,
      icon: 'sync',
      keywords: ['refresh', 're-read', 'workspace', 'file', 'edb'],
      run: () => syncActiveWorkspaceNow(),
    });
    api.ui.registerCommand({
      id: 'edb-file:sync-folder',
      title: 'Sync workspace folder',
      group: FILE_GROUP,
      icon: 'sync',
      keywords: ['refresh', 're-read', 'folder', 'file', 'all'],
      run: () => syncConnectedFolder(),
    });
    api.ui.registerCommand({
      id: 'edb-file:compare-file',
      title: 'Compare workspace with its file',
      group: FILE_GROUP,
      icon: 'difference',
      keywords: ['merge', 'diff', 'replicate', 'conflict', 'file'],
      run: () => compareWithOwnFile(),
    });
  }

  api.ui.registerCommand({
    id: 'edb-file:leave',
    title: 'Back to browser storage',
    group: FILE_GROUP,
    icon: 'logout',
    keywords: ['detach', 'forget', 'file'],
    run: () => leaveFileMode(),
  });

  void refreshFileCommands();
}

// The one-time "Storage has changed" notice used to live here. It told a
// returning user that their pre-SQLite IndexedDB data was NOT carried over, and
// pointed them at an older build to export from — advice a phone cannot follow,
// because there is no File System Access API to export through.
//
// `plugins/legacy-import.ts` replaces it with an offer to copy that data across,
// and owns the `easydb:legacy-idb-notice` flag this used to set.

/**
 * `?space=NAME` named a workspace this browser does not hold, and telling whether
 * `NAME.edb` is in the user's folder needs a permission grant.
 *
 * Asked HERE, not during boot: `requestPermission` resolves only from a user
 * gesture, and this dialog's button is the first gesture the page gets. Boot has
 * already created the workspace under that name, so declining leaves a working
 * app rather than a dead end.
 */
async function offerSpaceFolder(api: HostApi): Promise<void> {
  const requested = pendingSpaceRequest();
  if (requested === null) return;
  // Cleared before the awaits: a second `load()` (the Plugin Manager re-emits
  // `app:ready` on a hot install) must not ask the same question again.
  clearPendingSpaceRequest();
  const file = spaceFileName(requested);
  const ok = await api.ui.dialogs.confirm(`"${requested}" is not in this browser. Look for ${file} in your workspace folder?`, 'Open workspace');
  if (!ok) return;
  const dir = await workspaceFolder();
  if (!dir) return;
  if (!(await listWorkspaceFiles(dir)).includes(file)) {
    api.ui.dialogs.toast(`There is no ${file} in "${dir.name}".`, { kind: 'info' });
    return;
  }
  // Reloads on success. A stale listing is the only way this returns.
  await adoptFolderFile(requested);
}

/** Nothing the user put here: no tables and no views — `isEmptyWorkspace`'s rule. */
async function openWorkspaceIsEmpty(api: HostApi): Promise<boolean> {
  const workspaceId = api.workspaceId();
  if (!workspaceId) return false;
  const counted = await countWorkspaceContents(storeBridge(), workspaceId, { countRows: false }).catch(() => null);
  // A count that could not be taken is not a count of none: say it holds
  // something, so an unreadable store asks nothing rather than crying wolf.
  return counted !== null && isEmptyWorkspace(counted);
}

/**
 * Say so when the browser has taken the folder grant away, and offer it back.
 *
 * A permission does not always survive a restart: Chrome drops it unless the user
 * chose "Allow on every visit", and clearing site data drops it always. The tab
 * then boots on whatever the pool holds — for a workspace whose data lives in a
 * file, that is NOTHING — and every check here read the permission and threw the
 * answer away, so the app came up empty without a word. The data was never gone;
 * it was in a file the app was no longer allowed to open.
 *
 * Asking needs a user gesture, which a boot has not got. So this is a dialog: its
 * button IS the gesture, and `workspaceFolder()` re-requests the remembered handle
 * before falling back to the picker.
 *
 * A workspace that never had a file asks nothing — there is nothing it is missing.
 */
async function offerReconnect(api: HostApi, folderName: string | null, file: string | null): Promise<void> {
  const what = folderName ? `the workspace folder "${folderName}"` : `${file}`;
  const ok = await api.ui.dialogs.confirm(
    `easyDBAccess may no longer open ${what}, so this workspace is showing only what is stored in this browser. Your data is still in the file. Reconnect now?`,
    'Workspace folder',
  );
  if (!ok) return;
  const dir = await workspaceFolder();
  if (!dir) {
    api.ui.dialogs.toast('Still not connected. Run "Connect workspace folder" when you are ready.', { kind: 'info' });
    return;
  }
  api.ui.dialogs.toast(`Reconnected to "${dir.name}".`, { kind: 'success' });

  // Re-read the file ONLY when there is nothing here to lose. The grant is back,
  // so the file can be opened again — but replacing the open database with it is
  // exactly the wrong move if the user has since typed something into the empty
  // shell they were given. Sync is the deliberate way to do it in that case, and
  // it asks which copy wins.
  const active = adoptedFileName();
  if (!active) return;
  const workspaceId = api.workspaceId();
  const counted = workspaceId ? await countWorkspaceContents(storeBridge(), workspaceId, { countRows: false }).catch(() => null) : null;
  if (counted && isEmptyWorkspace(counted)) await reloadActiveFromFile(active);
}

/**
 * Ask the "which copy?" question that boot had to leave behind.
 *
 * Boot takes the browser's copy — the answer that destroys nothing — and records
 * the question, because `dialogs.choice` needs a shell boot has not built yet.
 * This is where it gets asked, at the first moment there is something to ask it
 * with, and it is the difference between a workspace that opens empty in silence
 * and one that says which two copies it found.
 *
 * Both outright answers are already done by the time this returns: taking the
 * file adopts it and reloads, and keeping this copy is what the tab already did.
 */
async function askPendingCopyQuestion(api: HostApi): Promise<void> {
  const pending = takeCopyQuestion();
  if (!pending) return;
  const answer = await settleCopyQuestion(api.ui.dialogs, pending.file, pending.workspaceId, pending.verdict);
  // Keeping this copy needs no work: it is the one the tab booted onto. A
  // dismissed dialog comes to the same thing, which is what makes dismissing safe.
  //
  // `reloadActiveFromFile`, not `adoptFolderFile`: boot already pointed this tab
  // at that file, so this is the Sync's "load the disk version" — re-read the
  // bytes underneath and reload — rather than an adopt of somewhere new. It is
  // also the one that does not spend the boot-loop guard on a file the user is
  // deliberately asking for.
  if (answer === 'file') await reloadActiveFromFile(pending.file);
  if (answer === 'compare') await session?.compareWithOwnFile();
}

/**
 * Run the comparison a "Compare them…" answer asked for before the reload.
 *
 * The comparison needs a live store bound to the workspace — `merge-file.ts`
 * opens the file BESIDE it — so it cannot run in the tab that asked. The marker
 * survives that one reload and is taken here.
 */
async function runRequestedComparison(): Promise<void> {
  const wanted = takeComparisonRequest();
  if (wanted === null) return;
  // Only for the file this tab actually landed on. A marker left by a switch that
  // went somewhere else would compare the wrong pair.
  if (wanted !== adoptedFileName()) return;
  await session?.compareWithOwnFile();
}

export async function load(api: HostApi): Promise<void> {
  if (!supported()) return;
  await offerSpaceFolder(api);
  await askPendingCopyQuestion(api);
  await runRequestedComparison();

  // The shell snapshots the registry during boot, and boot itself writes — the
  // workspace record, the seeded view templates — so the button can already be out
  // of date by its first paint. One push here settles it.
  session?.refreshSaveButton();

  // The file half. A workspace with no file needs no handle and no permission, but
  // everything below is about a file or a folder this browser has already been
  // given — and about noticing when it no longer has it.
  //
  // Re-check the folder before the file. A folder the user has allowed on every
  // visit covers everything in it, so Save then needs no prompt at all — and
  // asking about the folder once beats asking about each file.
  const folder = await rememberedFolder();
  // Permission is READ, not asked for: asking needs a gesture, and the workspace
  // has already loaded from the pool. A Chrome user with a persisted grant reads
  // back `granted` and saves silently.
  const folderOk = folder ? await ensureWritable(folder, false) : false;
  // A folder this browser is already allowed to read carries the device settings,
  // so they are adopted before anything asks for one.
  if (folder && folderOk) await adoptDeviceFolder(folder);
  let fileOk = false;
  if (adoptedFileName() !== null) {
    // `rememberedHandle()` is ONE origin-wide slot — whichever file was opened last
    // in any tab — so it is a candidate, not an answer. `ownFile` takes it only if
    // its name is the file this tab's marker names, and otherwise looks that file
    // up in the folder. Handing the raw slot to `setEdbHandle` is what had Save
    // writing one workspace into another workspace's file.
    const own = session ? await session.ownFile() : null;
    if (own) fileOk = await ensureWritable(own, false);
  }
  // This tab is looking at a FILE it can no longer open. The permission answers
  // were read and dropped on the floor until v0.0.434, which is how a workspace
  // whose data is in a file came up empty and silent after a restart.
  //
  // Two ways to be in trouble, and nothing else asks. A workspace with rows on
  // screen in a browser that merely forgot a folder is missing nothing, and a
  // question about it on every boot would be noise:
  //
  //  - this tab is looking at a FILE it can no longer open, or
  //  - the folder is unreachable AND the workspace is empty, which is the shape
  //    of the report: the data is in the folder and the app came up blank.
  //
  // Only where there is something to offer. Firefox and Safari have no directory
  // picker at all, so a file-backed workspace there is permanently in the `lostFile`
  // state by construction — and "reconnect?" would be a question with no button
  // behind it, asked on every single boot. Save already says what that browser can
  // and cannot do.
  const lostFile = adoptedFileName() !== null && !fileOk && !folderOk;
  const blankWithFolder = folder !== null && !folderOk && (await openWorkspaceIsEmpty(api));
  if (canPickFolder() && (lostFile || blankWithFolder)) {
    await offerReconnect(api, folder?.name ?? null, adoptedFileName());
  }

  // Autosave last, and for a workspace with or without a file: the timer must not
  // arm before the handle above is back, or the first tick would find none and ask
  // where to save a workspace that already has an answer.
  const on = await api.settings.get(meta.id, AUTOSAVE_KEY);
  if (on === true && session) {
    session.autosave.setEnabled(true);
    // So the palette says "Turn off autosave" rather than offering to turn on
    // what is already on.
    await session.refreshFileCommands();
    api.ui.dialogs.toast('Autosave is on for this workspace', { kind: 'info' });
  }
}
